"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type Difficulty,
  type Drafting,
  estimateWorkload,
  type Genre,
  hoursPerPage,
  type PageDensity,
  type Purpose,
  pagesPerHour,
  type WorkloadResult,
  type WritingDensity,
} from "@/lib/tool-kit/grades/workload";

/** English strings. A translated content file overrides these through `ui`. */
export const COURSE_WORKLOAD_ESTIMATOR_STRINGS = {
  termLabel: "Weeks in the term",
  readingHeading: "Reading",
  pagesLabel: "Pages per week",
  densityLabel: "Page density",
  densities: {
    "450": "450 words — paperback",
    "600": "600 words — monograph",
    "750": "750 words — textbook",
  },
  difficultyLabel: "Difficulty",
  difficulties: {
    none: "No new concepts",
    some: "Some new concepts",
    many: "Many new concepts",
  },
  purposeLabel: "Purpose",
  purposes: {
    survey: "Survey — skim for the main ideas",
    understand: "Understand — follow every sentence",
    engage: "Engage — work problems, question, evaluate",
  },
  ownReadingRate: "Your own rate in pages per hour (optional)",
  writingHeading: "Writing",
  writingPagesLabel: "Pages per term",
  writingDensityLabel: "Page density",
  writingDensities: {
    "250": "250 words — double-spaced",
    "500": "500 words — single-spaced",
  },
  genreLabel: "Genre",
  genres: {
    reflection: "Reflection or narrative",
    argument: "Argument",
    research: "Research",
  },
  draftingLabel: "Drafting",
  draftings: {
    none: "No drafting",
    minimal: "Minimal drafting — revised once",
    extensive: "Extensive drafting — revised several times",
  },
  ownWritingRate: "Your own rate in hours per page (optional)",
  examsHeading: "Exams and other work",
  examsLabel: "Exams per term",
  examHoursLabel: "Study hours per exam",
  otherLabel: "Other assignments per term",
  otherHoursLabel: "Hours per assignment",
  classHeading: "Class meetings",
  meetingsLabel: "Meetings per week",
  meetingLengthLabel: "Hours per meeting",
  results: "Estimated workload",
  total: "Total hours per week",
  independent: "Out of class",
  inClass: "In class",
  breakdownHeading: "Breakdown",
  colPart: "Part",
  colWeek: "Hours per week",
  colTerm: "Hours per term",
  parts: {
    reading: "Reading",
    writing: "Writing",
    exams: "Exam study",
    other: "Other assignments",
    classTime: "Class meetings",
  },
  ratesUsed:
    "Rates used: {reading} pages per hour and {writing} hours per written page.",
  invalid:
    "Enter a term of at least one week and numbers of 0 or more. A reading rate must be above 0.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary:
    "Estimated workload: {total} hours per week ({independent} out of class, {inClass} in class)",
} as const;

type Strings = typeof COURSE_WORKLOAD_ESTIMATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type Parts = keyof Strings["parts"];
const PARTS: readonly Parts[] = [
  "reading",
  "writing",
  "exams",
  "other",
  "classTime",
];

/** A count or an amount: blank is 0, anything else must be a number of 0 or more. */
function amount(raw: string): number | null {
  if (raw.trim() === "") {
    return 0;
  }
  const value = parseNumberInput(raw);
  return value !== null && value >= 0 ? value : null;
}

/** An optional override: blank means "use the table". */
function override(raw: string): number | null | undefined {
  return raw.trim() === "" ? undefined : amount(raw);
}

function options<K extends string>(labels: Record<K, string>) {
  return (Object.keys(labels) as K[]).map((value) => ({
    value,
    label: labels[value],
  }));
}

function ResultPanel({
  result,
  weeks,
  readingRate,
  writingRate,
  strings,
  fmt,
}: {
  result: WorkloadResult;
  weeks: number;
  readingRate: number;
  writingRate: number;
  strings: Strings;
  fmt: Format;
}) {
  const hours = (value: number) =>
    fmt(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.total} value={hours(result.total)} />
        <StatCard
          label={strings.independent}
          value={hours(result.independent)}
        />
        <StatCard label={strings.inClass} value={hours(result.classTime)} />
      </StatGrid>
      <p className="text-muted-foreground text-sm">
        {t(strings.ratesUsed, {
          reading: fmt(readingRate, { maximumFractionDigits: 2 }),
          writing: fmt(writingRate, { maximumFractionDigits: 2 }),
        })}
      </p>
      <h3 className={WIDGET_H3}>{strings.breakdownHeading}</h3>
      <DataTable
        head={[strings.colPart, strings.colWeek, strings.colTerm]}
        rows={PARTS.map((part) => [
          strings.parts[part],
          fmt(result[part], { maximumFractionDigits: 2 }),
          fmt(result[part] * weeks, { maximumFractionDigits: 1 }),
        ])}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summary, {
          total: hours(result.total),
          independent: hours(result.independent),
          inClass: hours(result.classTime),
        })}
      />
    </>
  );
}

export default function CourseWorkloadEstimatorWidget() {
  const strings = useToolUi(COURSE_WORKLOAD_ESTIMATOR_STRINGS);
  const fmt = useToolNumber();
  const [weeksRaw, setWeeksRaw] = useState("15");
  const [pagesRaw, setPagesRaw] = useState("60");
  const [density, setDensity] = useState<`${PageDensity}`>("600");
  const [difficulty, setDifficulty] = useState<Difficulty>("some");
  const [purpose, setPurpose] = useState<Purpose>("understand");
  const [ownReadingRaw, setOwnReadingRaw] = useState("");
  const [writingPagesRaw, setWritingPagesRaw] = useState("20");
  const [writingDensity, setWritingDensity] =
    useState<`${WritingDensity}`>("250");
  const [genre, setGenre] = useState<Genre>("argument");
  const [drafting, setDrafting] = useState<Drafting>("minimal");
  const [ownWritingRaw, setOwnWritingRaw] = useState("");
  const [examsRaw, setExamsRaw] = useState("2");
  const [examHoursRaw, setExamHoursRaw] = useState("6");
  const [otherRaw, setOtherRaw] = useState("4");
  const [otherHoursRaw, setOtherHoursRaw] = useState("3");
  const [meetingsRaw, setMeetingsRaw] = useState("2");
  const [meetingLengthRaw, setMeetingLengthRaw] = useState("1.25");

  const ownReading = override(ownReadingRaw);
  const ownWriting = override(ownWritingRaw);
  const readingRate =
    ownReading ??
    pagesPerHour(purpose, difficulty, Number(density) as PageDensity);
  const writingRate =
    ownWriting ??
    hoursPerPage(genre, drafting, Number(writingDensity) as WritingDensity);
  const values = [
    amount(weeksRaw),
    amount(pagesRaw),
    amount(writingPagesRaw),
    amount(examsRaw),
    amount(examHoursRaw),
    amount(otherRaw),
    amount(otherHoursRaw),
    amount(meetingsRaw),
    amount(meetingLengthRaw),
  ];
  const [
    weeks,
    pages,
    writingPages,
    exams,
    examHours,
    other,
    otherHours,
    meetings,
    meetingLength,
  ] = values;
  const ready =
    values.every((value) => value !== null) &&
    ownReading !== null &&
    ownWriting !== null;
  const result = ready
    ? estimateWorkload({
        weeks: weeks ?? 0,
        readingPagesPerWeek: pages ?? 0,
        readingRate,
        writingPagesPerSemester: writingPages ?? 0,
        writingRate,
        exams: exams ?? 0,
        studyHoursPerExam: examHours ?? 0,
        otherAssignments: other ?? 0,
        hoursPerAssignment: otherHours ?? 0,
        meetingsPerWeek: meetings ?? 0,
        meetingHours: meetingLength ?? 0,
      })
    : null;
  const numberField = (
    id: string,
    label: string,
    value: string,
    set: (next: string) => void
  ) => <TextField id={id} label={label} numeric onChange={set} value={value} />;

  return (
    <div className="space-y-6">
      <div className={CARD}>
        {numberField("cwe-weeks", strings.termLabel, weeksRaw, setWeeksRaw)}
      </div>

      <div className={`${CARD} space-y-4`}>
        <h3 className={WIDGET_H3}>{strings.readingHeading}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {numberField("cwe-pages", strings.pagesLabel, pagesRaw, setPagesRaw)}
          <SelectField
            id="cwe-density"
            label={strings.densityLabel}
            onChange={setDensity}
            options={options(strings.densities)}
            value={density}
          />
          <SelectField
            id="cwe-difficulty"
            label={strings.difficultyLabel}
            onChange={setDifficulty}
            options={options(strings.difficulties)}
            value={difficulty}
          />
          <SelectField
            id="cwe-purpose"
            label={strings.purposeLabel}
            onChange={setPurpose}
            options={options(strings.purposes)}
            value={purpose}
          />
        </div>
        {numberField(
          "cwe-own-reading",
          strings.ownReadingRate,
          ownReadingRaw,
          setOwnReadingRaw
        )}
      </div>

      <div className={`${CARD} space-y-4`}>
        <h3 className={WIDGET_H3}>{strings.writingHeading}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {numberField(
            "cwe-writing-pages",
            strings.writingPagesLabel,
            writingPagesRaw,
            setWritingPagesRaw
          )}
          <SelectField
            id="cwe-writing-density"
            label={strings.writingDensityLabel}
            onChange={setWritingDensity}
            options={options(strings.writingDensities)}
            value={writingDensity}
          />
          <SelectField
            id="cwe-genre"
            label={strings.genreLabel}
            onChange={setGenre}
            options={options(strings.genres)}
            value={genre}
          />
          <SelectField
            id="cwe-drafting"
            label={strings.draftingLabel}
            onChange={setDrafting}
            options={options(strings.draftings)}
            value={drafting}
          />
        </div>
        {numberField(
          "cwe-own-writing",
          strings.ownWritingRate,
          ownWritingRaw,
          setOwnWritingRaw
        )}
      </div>

      <div className={`${CARD} space-y-4`}>
        <h3 className={WIDGET_H3}>{strings.examsHeading}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {numberField("cwe-exams", strings.examsLabel, examsRaw, setExamsRaw)}
          {numberField(
            "cwe-exam-hours",
            strings.examHoursLabel,
            examHoursRaw,
            setExamHoursRaw
          )}
          {numberField("cwe-other", strings.otherLabel, otherRaw, setOtherRaw)}
          {numberField(
            "cwe-other-hours",
            strings.otherHoursLabel,
            otherHoursRaw,
            setOtherHoursRaw
          )}
        </div>
      </div>

      <div className={`${CARD} space-y-4`}>
        <h3 className={WIDGET_H3}>{strings.classHeading}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {numberField(
            "cwe-meetings",
            strings.meetingsLabel,
            meetingsRaw,
            setMeetingsRaw
          )}
          {numberField(
            "cwe-meeting-length",
            strings.meetingLengthLabel,
            meetingLengthRaw,
            setMeetingLengthRaw
          )}
        </div>
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result === null || weeks === null ? (
          <Notice>{strings.invalid}</Notice>
        ) : (
          <ResultPanel
            fmt={fmt}
            readingRate={readingRate}
            result={result}
            strings={strings}
            weeks={weeks}
            writingRate={writingRate}
          />
        )}
      </section>
    </div>
  );
}
